import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { SectionHeadingComponent } from '../../components/section-heading/section-heading.component';
import { LayoutContainerComponent } from '../../components/layout-container/layout-container.component';
import { CardSliderComponent, type CardSliderItem } from '../../components/card-slider/card-slider.component';

@Component({
  selector: 'app-services',
  standalone: true,
  imports: [RouterLink, SectionHeadingComponent, LayoutContainerComponent, CardSliderComponent],
  templateUrl: './services.component.html',
  styleUrl: './services.component.css'
})
export class ServicesComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly howItWorksConfig = environment.servicesPage.sectionHowItWorks;

  readonly howItWorksVideoUrl = this.buildMediaUrl(
    this.howItWorksConfig.path,
    this.howItWorksConfig.webmUrl
  );
  readonly howItWorksHighlights = [
    'A Custom Podium for a "head-to-head" gaming experience.',
    'Questions and Answers in multiple rounds with multiple game flows.',
    'User selectable themes and media content provided by players.',
    'Guaranteed to be engaging, fun and create more unity among teams.',
    'Individual Player and Team results for each round.'
  ];

  private buildMediaUrl(path: string, fileName: string): string {
    const normalizedPrefix = environment.blobPrefix.endsWith('/')
      ? environment.blobPrefix
      : `${environment.blobPrefix}/`;
    const normalizedPath = path
      .replace(/\\+/g, '/')
      .replace(/^\/+/, '')
      .replace(/\/+$/, '');

    return `${normalizedPrefix}${normalizedPath}/${fileName}`;
  }

  private createCardIconSvg(innerMarkup: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(`
      <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <rect x="8" y="12" width="48" height="44" rx="10" fill="var(--color-accent)" />
        <rect x="13" y="24" width="38" height="27" rx="6" fill="var(--color-surface-1)" />
        ${innerMarkup}
        <rect x="18" y="8" width="4" height="10" rx="2" fill="var(--color-accent)" />
        <rect x="42" y="8" width="4" height="10" rx="2" fill="var(--color-accent)" />
      </svg>
    `);
  }

  private readonly calendarIconSvg = this.createCardIconSvg(`
    <rect x="18" y="30" width="8" height="8" rx="2" fill="var(--color-text)" />
    <rect x="30" y="30" width="8" height="8" rx="2" fill="var(--color-text)" />
    <rect x="42" y="30" width="4" height="8" rx="2" fill="var(--color-text)" />
    <rect x="18" y="42" width="8" height="4" rx="2" fill="var(--color-text)" />
    <rect x="30" y="42" width="16" height="4" rx="2" fill="var(--color-text)" />
  `);

  private readonly registerIconSvg = this.createCardIconSvg(`
    <circle cx="24" cy="34" r="4" fill="var(--color-text)" />
    <path d="M18 44c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" fill="none" />
    <path d="M36 34h10M41 29v10" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
  `);

  private readonly accessIconSvg = this.createCardIconSvg(`
    <rect x="20" y="34" width="18" height="12" rx="2" fill="var(--color-text)" />
    <path d="M24 34v-3a5 5 0 0 1 10 0v3" stroke="var(--color-text)" stroke-width="3" fill="none" stroke-linecap="round" />
    <circle cx="29" cy="40" r="1.8" fill="var(--color-surface-1)" />
    <path d="M42 40h5" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
  `);

  private readonly eventIconSvg = this.createCardIconSvg(`
    <rect x="18" y="30" width="8" height="8" rx="2" fill="var(--color-text)" />
    <rect x="30" y="30" width="8" height="8" rx="2" fill="var(--color-text)" />
    <path d="M19 44h26" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
    <path d="M38 44l3-4 3 4 4 1-3 3 .7 4-4-2-4 2 .8-4-3-3z" fill="var(--color-text)" />
  `);

  private readonly podiumIconSvg = this.createCardIconSvg(`
    <path d="M20 45h24M24 45v-8h16v8" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none" />
    <path d="M24 34h16l-2-6H26z" fill="var(--color-text)" />
    <path d="M46 32l4 3-4 3" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none" />
  `);

  private readonly questionsIconSvg = this.createCardIconSvg(`
    <path d="M27 35a5 5 0 1 1 8 4v2" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none" />
    <circle cx="35" cy="45" r="2" fill="var(--color-text)" />
    <circle cx="23" cy="32" r="1.6" fill="var(--color-text)" />
    <circle cx="20" cy="38" r="1.6" fill="var(--color-text)" />
  `);

  private readonly answersIconSvg = this.createCardIconSvg(`
    <path d="M20 37l6 6 14-14" stroke="var(--color-text)" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" fill="none" />
    <path d="M42 36h6M42 42h4" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
  `);

  private readonly teamsIconSvg = this.createCardIconSvg(`
    <circle cx="24" cy="33" r="4" fill="var(--color-text)" />
    <circle cx="40" cy="33" r="4" fill="var(--color-text)" />
    <path d="M18 44c0-3 2.7-5.5 6-5.5s6 2.5 6 5.5M34 44c0-3 2.7-5.5 6-5.5s6 2.5 6 5.5" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" fill="none" />
  `);

  private readonly optionsIconSvg = this.createCardIconSvg(`
    <path d="M20 32h24M20 38h24M20 44h24" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
    <circle cx="48" cy="32" r="3" fill="var(--color-text)" />
    <circle cx="42" cy="38" r="3" fill="var(--color-text)" />
    <circle cx="46" cy="44" r="3" fill="var(--color-text)" />
  `);

  private readonly shippingIconSvg = this.createCardIconSvg(`
    <rect x="18" y="34" width="16" height="8" rx="2" fill="var(--color-text)" />
    <path d="M34 36h8l4 4v2h-12z" fill="var(--color-text)" />
    <circle cx="24" cy="45" r="3" fill="var(--color-text)" />
    <circle cx="40" cy="45" r="3" fill="var(--color-text)" />
  `);

  private readonly assembleIconSvg = this.createCardIconSvg(`
    <rect x="18" y="30" width="10" height="10" rx="2" fill="var(--color-text)" />
    <rect x="31" y="30" width="10" height="10" rx="2" fill="var(--color-text)" />
    <path d="M25 44h22" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
    <path d="M46 40l4 4-4 4" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none" />
  `);

  private readonly loadConfigIconSvg = this.createCardIconSvg(`
    <path d="M20 33h24" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
    <path d="M32 29v12" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
    <path d="M29 39l3 3 3-3" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none" />
    <rect x="20" y="43" width="24" height="4" rx="2" fill="var(--color-text)" />
  `);

  private readonly enjoyIconSvg = this.createCardIconSvg(`
    <circle cx="26" cy="34" r="2.5" fill="var(--color-text)" />
    <circle cx="38" cy="34" r="2.5" fill="var(--color-text)" />
    <path d="M22 40c2 4 5.3 6 10 6s8-2 10-6" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" fill="none" />
    <path d="M46 28l1.8 3.4 3.8.6-2.7 2.8.6 3.9-3.5-1.7-3.4 1.7.6-3.9-2.7-2.8 3.8-.6z" fill="var(--color-text)" />
  `);

  private readonly medicalIndustryIconSvg = this.createCardIconSvg(`
    <rect x="29" y="30" width="6" height="16" rx="2" fill="var(--color-text)" />
    <rect x="24" y="35" width="16" height="6" rx="2" fill="var(--color-text)" />
    <path d="M22 25h20" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
  `);

  private readonly officeIndustryIconSvg = this.createCardIconSvg(`
    <rect x="20" y="30" width="24" height="12" rx="2" fill="none" stroke="var(--color-text)" stroke-width="3" />
    <path d="M32 42v4M25 46h14" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
    <rect x="24" y="34" width="5" height="4" rx="1" fill="var(--color-text)" />
  `);

  private readonly universityIndustryIconSvg = this.createCardIconSvg(`
    <path d="M18 32l14-6 14 6-14 6z" fill="var(--color-text)" />
    <path d="M24 36v5c0 2 3.5 4 8 4s8-2 8-4v-5" stroke="var(--color-text)" stroke-width="3" fill="none" stroke-linecap="round" />
    <path d="M46 33v7" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
  `);

  private readonly professionalIndustryIconSvg = this.createCardIconSvg(`
    <rect x="20" y="31" width="24" height="14" rx="2" fill="none" stroke="var(--color-text)" stroke-width="3" />
    <path d="M28 31v-3h8v3" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
    <path d="M28 38h8" stroke="var(--color-text)" stroke-width="3" stroke-linecap="round" />
  `);

  private readonly factoryIndustryIconSvg = this.createCardIconSvg(`
    <path d="M20 45V34l8 4v-4l8 4v-7h8v14z" fill="var(--color-text)" />
    <rect x="40" y="25" width="4" height="8" rx="1" fill="var(--color-text)" />
    <circle cx="30" cy="40" r="1.5" fill="var(--color-surface-1)" />
    <circle cx="35" cy="40" r="1.5" fill="var(--color-surface-1)" />
  `);

  readonly industryHighlights = [
    {
      title: 'Medical',
      inlineSvg: this.medicalIndustryIconSvg
    },
    {
      title: 'Office',
      inlineSvg: this.officeIndustryIconSvg
    },
    {
      title: 'University',
      inlineSvg: this.universityIndustryIconSvg
    },
    {
      title: 'Professional',
      inlineSvg: this.professionalIndustryIconSvg
    },
    {
      title: 'Factory',
      inlineSvg: this.factoryIndustryIconSvg
    }
  ];

  readonly serviceCards = [
    {
      title: 'Team Building Experiences',
      text: 'Interactive engaging events and tailored by you to strengthen communication, collaboration, and morale.'
    },
    {
      title: 'Return On Investment',
      text: 'Helps reinforce recent training or displays current teams collective experience in a fun and engaging manor.'
    },
    {
      title: 'Planning & Coordination',
      text: 'Professional event and logistics support that helps turn ideas into organized, memorable experiences.'
    }
  ];

  readonly processSteps: CardSliderItem[] = [
    {
      id: 'check-date',
      number: '01',
      title: 'Check Date',
      image: 'https://unitedteambuildingdev.blob.core.windows.net/site-assets/calendar-01.svg',
      inlineSvg: this.calendarIconSvg,
      imageAlt: 'Check Date step icon',
      text: 'Check if the date is available.'
    },
    {
      id: 'register',
      number: '02',
      title: 'Register',
      image: '/assets/register-01.svg',
      inlineSvg: this.registerIconSvg,
      imageAlt: 'Register step icon',
      text: 'Fill out a Booking Request.'
    },
    {
      id: 'access',
      number: '03',
      title: 'Access',
      image: '/assets/access-01.svg',
      inlineSvg: this.accessIconSvg,
      imageAlt: 'Access step icon',
      text: 'Join your new organization.'
    },
    {
      id: 'create-event',
      number: '04',
      title: 'Create Event',
      image: '/assets/event-01.svg',
      inlineSvg: this.eventIconSvg,
      imageAlt: 'Create Event step icon',
      text: 'Create your event or multiple events.'
    },
    {
      id: 'pick-podium',
      number: '05',
      title: 'Pick Podium',
      image: '/assets/podium-01.svg',
      inlineSvg: this.podiumIconSvg,
      imageAlt: 'Pick Podium step icon',
      text: 'Choose a podium or multiple podiums.'
    },
    {
      id: 'build-questions',
      number: '06',
      title: 'Build Questions',
      image: '/assets/questions-01.svg',
      inlineSvg: this.questionsIconSvg,
      imageAlt: 'Build Questions step icon',
      text: 'Create your own custom questions.'
    },
    {
      id: 'build-answers',
      number: '07',
      title: 'Build Answers',
      image: '/assets/answers-01.svg',
      inlineSvg: this.answersIconSvg,
      imageAlt: 'Build Answers step icon',
      text: 'Create your answers.'
    },
    {
      id: 'build-teams',
      number: '08',
      title: 'Build Teams',
      image: '/assets/teams-01.svg',
      inlineSvg: this.teamsIconSvg,
      imageAlt: 'Build Teams step icon',
      text: 'Create teams and members.'
    },
    {
      id: 'config-options',
      number: '09',
      title: 'Configure Options',
      image: '/assets/options-01.svg',
      inlineSvg: this.optionsIconSvg,
      imageAlt: 'Configure Options step icon',
      text: 'Choose from various options and features'
    },
    {
      id: 'ship-podium',
      number: '10',
      title: 'Ship Podium',
      image: '/assets/shipping-01.svg',
      inlineSvg: this.shippingIconSvg,
      imageAlt: 'Ship Podium step icon',
      text: 'Ship your podium(s) to the desired location.'
    },
    {
      id: 'assemble-podium',
      number: '11',
      title: 'Assemble Podium',
      image: '/assets/assemble-01.svg',
      inlineSvg: this.assembleIconSvg,
      imageAlt: 'Assemble Podium step icon',
      text: 'Assemble your podium(s).'
    },
    {
      id: 'load-config',
      number: '12',
      title: 'Load Config',
      image: '/assets/UTB-Logo.svg',
      inlineSvg: this.loadConfigIconSvg,
      imageAlt: 'Load Config step icon',
      text: 'Load your configuration(s).'
    },
    {
      id: 'have-fun',
      number: '13',
      title: 'Have Fun',
      image: '/assets/enjoy-01.svg',
      inlineSvg: this.enjoyIconSvg,
      imageAlt: 'Have Fun step icon',
      text: 'Enjoy the experience and have fun!'

    }
  ];
}
